import React, { useMemo } from "react";
import {
  LogChangeType,
  LogData,
  LogDelta,
  Method
} from "@/entities/history/types";
import { getPeopleIdsDelta } from "@/entities/history/helpers";
import UserAvatar from "@/components/controls/UserAvatar";
import { useUsers } from "@/entities/users/hooks";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Ellipse, DreamhubGradient } from "@dreamhub/designsystem/icons";
import { formatISOToDateTimeString } from "@/utils/datetime";
import { useTranslation } from "react-i18next";
import { getEnumTKey } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import HighlightedText from "@/components/controls/HighlightedText";
import LogAction from "@/components/layouts/History/LogAction";
import { useSearchHighlight } from "@/contexts/SearchHighlightContext";
import { keys, map } from "lodash";
import { APIFieldItem } from "@/pages/settings/types";

const TRANSLATION_PREFIX = "detailedView.history.logGroup";

const getNumOfLogs = (logDelta: LogDelta | null) => {
  if (!logDelta) return 0;

  return Object.values(logDelta).reduce((total, value) => {
    if (Array.isArray(value)) {
      return total + value.length;
    }
    return total + 1;
  }, 0);
};

interface Props {
  entityType: EntityType;
  session: LogData;
  entityFieldsMap: Record<string, APIFieldItem>;
}

const LogSession: React.FC<Props> = ({
  entityType,
  session,
  entityFieldsMap
}) => {
  const { searchQuery } = useSearchHighlight();
  const { t } = useTranslation();
  const { userId, timeChange, logDelta, method, changeType } = session;
  const userData = useUsers([userId]).users[0];
  const numOfLogs = useMemo(() => getNumOfLogs(logDelta), [logDelta]);
  const isSingleChange = method === Method.INSERT ? true : numOfLogs === 1;
  const peopleIdsDelta = getPeopleIdsDelta(logDelta);

  const isDreamer = userId === null;
  const displayName = isDreamer ? t("dreamer") : userData?.displayName || "";

  const groupTitle = useMemo(() => {
    const titlePrefix = displayName;

    if (isSingleChange) {
      let action = t(
        `${TRANSLATION_PREFIX}.title.single.${method === Method.INSERT ? "insert" : "update"}`
      );
      if (peopleIdsDelta?.length > 0) {
        action = t(`${TRANSLATION_PREFIX}.title.single.added`);
      }
      const entityName = (
        changeType === LogChangeType.REVENUE_ITEMS
          ? t("entities.names.service")
          : t(getEnumTKey("entities", "names", EntityType, entityType))
      ).toLowerCase();

      if (method === Method.INSERT && userId === "") {
        return t(`${TRANSLATION_PREFIX}.title.single.creationDate`, {
          entity: entityName
        });
      }

      return (
        <div className="flex items-center">
          <HighlightedText text={titlePrefix} query={searchQuery} />
          <span className="flex items-center text-primary ml-1">
            <HighlightedText text={action} query={searchQuery} />
            <span className="ml-1 flex flex-col gap-1">
              {method === Method.INSERT ? (
                <HighlightedText text={entityName} query={searchQuery} />
              ) : logDelta ? (
                map(keys(logDelta), (fieldName) => {
                  const fieldDelta = logDelta?.[fieldName];
                  return map(fieldDelta, (delta, deltaIndex) => (
                    <LogAction
                      key={`${fieldName}-${deltaIndex}`}
                      entityType={entityType}
                      changeType={changeType}
                      fieldName={fieldName}
                      delta={delta}
                      peopleIdsDelta={peopleIdsDelta}
                      isSingleChange={isSingleChange}
                      fieldConfig={entityFieldsMap[fieldName]}
                    />
                  ));
                })
              ) : null}
            </span>
          </span>
        </div>
      );
    }

    // Multiple changes
    const multipleText = `${titlePrefix} ${t(`${TRANSLATION_PREFIX}.title.multiple`, { num: numOfLogs })}`;
    return <HighlightedText text={multipleText} query={searchQuery} />;
  }, [
    displayName,
    isSingleChange,
    entityType,
    method,
    userId,
    t,
    searchQuery,
    logDelta,
    changeType,
    numOfLogs,
    entityFieldsMap,
    peopleIdsDelta
  ]);

  return (
    <div className="flex flex-col gap-2 pl-2">
      <div className="flex gap-2 items-center">
        {isDreamer ? (
          <IconContainer className="size-[22px]">
            <DreamhubGradient />
          </IconContainer>
        ) : (
          <UserAvatar userData={userData} size="M" />
        )}
        <Text variant="bodySecondary">{groupTitle}</Text>
        <IconContainer className="text-grey-500">
          <Ellipse />
        </IconContainer>
        <Text variant="bodyTertiary">
          <HighlightedText
            text={formatISOToDateTimeString(timeChange)}
            query={searchQuery}
          />
        </Text>
      </div>
      <div className="relative min-h-[5px] pl-[30px] flex flex-col gap-1">
        <div className="connecting-line absolute left-2 -top-1 translate-x-[2px] w-px h-[calc(100%+25px)] bg-border-border1" />
        <div className="flex flex-col gap-1">
          {logDelta &&
            numOfLogs > 1 &&
            map(keys(logDelta), (fieldName) => {
              const fieldDelta = logDelta?.[fieldName];
              return map(fieldDelta, (delta, deltaIndex) => (
                <LogAction
                  key={`${fieldName}-${deltaIndex}`}
                  entityType={entityType}
                  changeType={changeType}
                  fieldName={fieldName}
                  delta={delta}
                  peopleIdsDelta={peopleIdsDelta}
                  isSingleChange={isSingleChange}
                  fieldConfig={entityFieldsMap[fieldName]}
                />
              ));
            })}
        </div>
      </div>
    </div>
  );
};

export default LogSession;
